import React, { useEffect, useRef, useState } from "react"
import { View } from "react-native"
import { useNavigation } from "@react-navigation/native"
import { connect } from "react-redux"
import { StatusBar } from "@common/systemBars/StatusBar"
import { FormStepper } from "@common/components/stepper/FormStepper"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { subscriptionService } from "@data/services/subscriptionService"
import { SchoolSubjectBadge } from "../tutor/students/ProfilDetails"
import { filterSchoolSubjectsByClass } from "@data/utility/Utils"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { DiagnosticSummaryModal } from "./DiagnosticSummaryModal"
import { fastTrackService } from "../data/services/fastTrackService"
import fastTrackBookTrialSlotStep from "./fastTrackBookTrialSlotStep"
import { useGetFastTrackUserOnMount } from "./hooks/useGetFastTrackUserOnMount"
import { FastTrackAssessmentCheckStep } from "./FastTrackAssessmentCheckStep"
import { useHasFinishedFastTrack } from "./hooks/useHasFinishedFastTrack"
import SchoolAssessmentTopicForm, {
    getSchoolAssessmentFormData,
} from "../school-assessment/SchoolAssessmentTopicForm"
import { AssessmentNeedsModal } from "./AssessmentNeedsModal"

const FastTrackDiagnosticScreen = (props) => {
    const navigation = useNavigation()
    const goToNextStepRef = useRef(null)

    const [summaryVisible, setSummaryVisible] = useState(false)
    const [selectedSession, setSelectedSession] = useState(null)
    const [isAssessmentPrepare, setIsAssessmentPrepare] = useState(false)
    const [assessmentNeedsVisible, setAssessmentNeedsVisible] = useState(false)
    const [assessmentData, setAssessmentData] = useState({
        ...getSchoolAssessmentFormData(null),
        assessmentNeeds: "",
    })
    const [objectivesWithFormulatedGoals, setObjectivesWithFormulatedGoals] =
        useState([])

    const { user, initialLoading, email, emailB64, hasFinishedFastTrack } =
        useGetFastTrackUserOnMount(props)

    const hoursPerWeek = 3

    const [formValues, setFormValues] = useState({})

    useApiStatus(subscriptionService.getSubscribePricing, null, false, () => {})

    // Handle AI formulate responses
    useApiStatus(
        subscriptionService.formulateObjective,
        null,
        false,
        (data, allData, requestUrlParams, requestRouteParams) => {
            const objectiveIndex = requestRouteParams?.objectiveIndex
            const formulatedGoal = data?.formulated_goal

            if (typeof objectiveIndex === "number" && formulatedGoal) {
                // Update the objective with the formulated goal
                setObjectivesWithFormulatedGoals((prev) => {
                    const updated = [...prev]
                    if (updated[objectiveIndex]) {
                        updated[objectiveIndex] = {
                            ...updated[objectiveIndex],
                            formulated_goal: formulatedGoal,
                        }
                    }
                    return updated
                })
            }

            // Loading state is handled by Redux automatically
        },
        (error, meta) => {
            // Handle error - loading state is handled by Redux automatically
            const objectiveIndex = meta?.objectiveIndex
            console.log(
                `Failed to formulate objective ${objectiveIndex}:`,
                error
            )
        }
    )

    // Static form data with conditional visibility
    const supportType = formValues?.support_type

    const studentClass = formValues?.student_class

    const filteredSchoolSubjects = filterSchoolSubjectsByClass(
        props.schoolSubjects,
        studentClass
    )

    const submitFastTrackDiagnostic = () => {
        const transformedData = transformFormDataToObjectives(formValues)
        const finalData = { ...transformedData, email }

        props.apiRequest(
            fastTrackService.submitFastTrackDiagnostic,
            {},
            finalData
        )
    }

    const formData = [
        // TODO: Re-enable assessment check step when ready
        {
            type: "custom",
            is_masked: true, // Hidden for now
            component: FastTrackAssessmentCheckStep,
            componentProps: {
                user,
                onAssessmentPrepare: () => {
                    setIsAssessmentPrepare(true)
                    goToNextStepRef.current()
                },
                onClassicSupport: () => {
                    setIsAssessmentPrepare(false)
                    goToNextStepRef.current()
                },
            },
        },
        // Support type step
        {
            label: "Accompagnement souhaité",
            is_masked: false, // Was: isAssessmentPrepare - always show now
            description:
                "Pour préparer votre séance découverte, indiquez l'accompagnement que vous souhaitez pour " +
                user?.firstname,
            name: "support_type",
            type: "select.single",
            options: [
                {
                    label: "Soutien sur une ou plusieurs matières",
                    value: "subject_support",
                    description:
                        "Renforcement ciblé pour améliorer les résultats",
                },
                {
                    label: "Aide aux devoirs",
                    value: "homework_help",
                    description:
                        "Accompagnement sur plusieurs matières pour les devoirs quotidiens",
                },
            ],
            defaultValue: null,
            validationRules: { required: true },
            height: 90,
            showNextButton: false,
        },
        // Assessment process
        {
            label: "Ajouter un contrôle",
            description:
                `Pour mieux préparer le contrôle de ${user?.firstname}, décrivez le sujet.`,
            name: "assessment_process",
            type: "custom",
            is_masked: !isAssessmentPrepare,
            component: SchoolAssessmentTopicForm,
            componentProps: {
                schoolSubjects: filteredSchoolSubjects,
                studentClass: user?.profile?.class,
                setHeaderStyle: () => ({ padding: 0 }),
                previousAssessment: assessmentData,
                formData: assessmentData,
                setFormData: setAssessmentData,
                onSubmit: () => setAssessmentNeedsVisible(true),
            },
        },
        // Subject selection step for subject_support flow only
        {
            label: "Matières souhaitées",
            name: "selected_subjects",
            type: "select.multiple",
            maxSelection: hoursPerWeek,
            is_masked: supportType !== "subject_support",
            options: (filteredSchoolSubjects || []).map((s) => ({
                label: s.name,
                value: s.id,
                icon: s.icon,
            })),
            validationRules: {
                minLength: supportType === "subject_support" ? 1 : 0,
                customCondition: (value) => {
                    if (supportType !== "subject_support") return true
                    return Array.isArray(value) && value.length >= 1
                },
            },
            description:
                "Pour préparer votre cours découverte, indiquez la (ou les) matière(s) que vous souhaitez pour " +
                user?.firstname,
            topComponent: (() => {
                const selectedCount = formValues?.selected_subjects?.length || 0
                const isLimitReached = selectedCount >= hoursPerWeek

                if (isLimitReached) {
                    return (
                        <TextM
                            color={theme.colors.accentDark}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            ✓ Limite atteinte ({selectedCount}/{hoursPerWeek}{" "}
                            matière{selectedCount > 1 ? "s" : ""})
                        </TextM>
                    )
                } else {
                    return (
                        <TextM
                            color={theme.colors.grey}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            {selectedCount} matière
                            {selectedCount > 1 ? "s" : ""} choisie
                            {selectedCount > 1 ? "s" : ""} • Chaque matière
                            nécessite au moins 1h/semaine
                        </TextM>
                    )
                }
            })(),
            showNextButton: !!formValues?.selected_subjects?.length,
        },
        // Generate dynamic needs selection steps for each selected subject (subject_support flow only)
        ...(() => {
            if (supportType !== "subject_support") return []

            const selectedSubjects = formValues?.selected_subjects || []

            return selectedSubjects.map((subjectId) => {
                const subject = (filteredSchoolSubjects || []).find(
                    (s) => s.id === subjectId
                )

                return {
                    label: `Objectifs de ${user?.firstname}`,
                    name: `needs_subject_${subjectId}`,
                    type: "select.multiple",
                    maxSelection: 2,
                    options: (props.objectiveNeeds || []).map((need) => ({
                        label: need.label,
                        value: need.key,
                        leftComponent: need.icon ? (
                            <FontAwesome5
                                name={need.icon}
                                size={20}
                                color={theme.colors.primary}
                                style={{ marginRight: 12 }}
                            />
                        ) : null,
                    })),
                    defaultValue: [],
                    validationRules: {
                        customCondition: (value) =>
                            !Array.isArray(value) || value.length <= 2,
                    },
                    topComponent: (
                        <View
                            style={{
                                marginBottom: 10,
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                flexWrap: "wrap",
                            }}
                        >
                            <TextM fontWeight="Bold" fontSize={14}>
                                En
                            </TextM>
                            <SchoolSubjectBadge
                                iconWidth={16}
                                fontSize={14}
                                style={{ maxWidth: null, marginLeft: 4 }}
                                schoolSubject={subject}
                            />
                            <TextM fontWeight="Bold" fontSize={14}>
                                , {user?.firstname} a besoin de...
                            </TextM>
                        </View>
                    ),
                    bottomComponent: (
                        <TextM
                            color={theme.colors.grey}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            Sélectionnez jusqu&apos;à 2 besoins pour cette
                            matière
                        </TextM>
                    ),
                }
            })
        })(),
        // Priority subjects step for homework_help flow only
        {
            label: "Matières les plus prioritaires pour les devoirs",
            name: "priority_subjects",
            type: "select.multiple",
            is_masked: supportType !== "homework_help",
            maxSelection: hoursPerWeek,
            options: (filteredSchoolSubjects || []).map((s) => ({
                label: s.name,
                value: s.id,
                icon: s.icon,
            })),
            validationRules: {
                minLength: supportType === "homework_help" ? 1 : 0,
                customCondition: (value) => {
                    if (supportType !== "homework_help") return true
                    return Array.isArray(value) && value.length >= 1
                },
            },
            description: `Pour préparer votre séance découverte, indiquez la (ou les) matière(s) que ${user?.firstname} doit travailler en priorité`,
            topComponent: (() => {
                const selectedCount = formValues?.priority_subjects?.length || 0
                const isLimitReached = selectedCount >= hoursPerWeek

                if (isLimitReached) {
                    return (
                        <TextM
                            color={theme.colors.success}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            ✓ Limite atteinte ({selectedCount}/{hoursPerWeek}{" "}
                            matières) • Le tuteur pourra aider sur d&apos;autres
                            matières selon les devoirs du jour
                        </TextM>
                    )
                } else {
                    return (
                        <TextM
                            color={theme.colors.grey}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            {selectedCount} matières prioritaires choisies • Le
                            tuteur pourra aider sur d&apos;autres matières selon
                            les devoirs du jour
                        </TextM>
                    )
                }
            })(),
            showNextButton: true,
        },
        // Book trial slot step
        {
            label:
                "Choisissez la date de votre cours découverte pour " +
                user?.firstname,
            name: "available_sessions",
            type: "custom",
            description: `Ce premier cours permet de découvrir l'expérience Masteur et de préciser le diagnostic. C'est offert et sans engagement !`,
            component: fastTrackBookTrialSlotStep,
            componentProps: { selectedSession, setSelectedSession },
            showNextButton: !!selectedSession,
            onReachStep: submitFastTrackDiagnostic,
        },
    ]

    // Transform form data back to expected objectives format
    const transformFormDataToObjectives = (formData) => {
        const objectives = []
        const supportType = formData.support_type

        if (supportType === "homework_help") {
            // Create a single homework objective with all selected subjects

            const prioritySubjects = formData.priority_subjects || []

            if (prioritySubjects.length > 0) {
                objectives.push({
                    type: "homework",
                    subject_ids: prioritySubjects,
                    allocated_hours: hoursPerWeek,
                    needs: [], // homework help doesn't need specific needs
                    status: "ongoing",
                    formulated_goal:
                        "Faire ses devoirs en " +
                        prioritySubjects
                            .map(
                                (subjectId) =>
                                    (filteredSchoolSubjects || []).find(
                                        (s) => s.id === subjectId
                                    )?.name
                            )
                            .join(", "),
                })
            }
        } else if (supportType === "subject_support") {
            // Create one objective per selected subject with allocated hours
            const selectedSubjects = formData.selected_subjects || []
            let hoursAllocation = formData.subject_hours_allocation || {}

            // If no allocation data (step was skipped), generate it automatically
            if (
                Object.keys(hoursAllocation).length === 0 &&
                selectedSubjects.length > 0
            ) {
                const baseHours = Math.floor(
                    hoursPerWeek / selectedSubjects.length
                )
                const extraHours = hoursPerWeek % selectedSubjects.length

                hoursAllocation = {}
                selectedSubjects.forEach((subjectId, index) => {
                    hoursAllocation[subjectId] =
                        baseHours + (index < extraHours ? 1 : 0)
                })
            }

            selectedSubjects.forEach((subjectId) => {
                const allocatedHours = hoursAllocation[subjectId] || 1
                const subjectNeeds =
                    formData[`needs_subject_${subjectId}`] || []
                const subject = (filteredSchoolSubjects || []).find(
                    (s) => s.id === subjectId
                )

                objectives.push({
                    type: "level_up",
                    subject_ids: [subjectId],
                    allocated_hours: allocatedHours,
                    needs: subjectNeeds,
                    status: "ongoing",
                    formulated_goal: "Améliorer son niveau en " + subject.name,
                })
            })
        }

        if (!isAssessmentPrepare) return { ...formData, objectives }

        return {
            support_type: "subject_support",
            objectives: [
                {
                    type: "level_up",
                    subject_ids: [assessmentData.selectedSubjectId],
                    allocated_hours: 2,
                    needs: ["preparer_un_examen"],
                },
            ],
            assessment: {
                school_subject_id: assessmentData.selectedSubjectId,
                date: assessmentData.date,
                key_requirements: assessmentData.keyRequirementsText,
                chapter_ids: assessmentData.selectedChapterIds,
                custom_chapters: assessmentData.customChapters,
                assessment_needs: assessmentData.assessmentNeeds,
            },
        }
    }

    // Function to call AI formulate for each objective using Redux patterns
    const formulateObjectivesWithAI = (objectives) => {
        setObjectivesWithFormulatedGoals(objectives) // Initialize with current objectives

        // Make API calls for each objective using Redux pattern
        objectives.forEach((objective, i) => {
            const payload = {
                email,
                type: objective.type,
                subject_ids: objective.subject_ids,
                needs: objective.needs || [],
                comment: objective.comment || null,
            }

            // Use the standard Redux API pattern
            props.apiRequest(
                subscriptionService.formulateObjective,
                { objectiveIndex: i },
                payload
            )
        })
    }

    const showRecapModal = (data) => {
        setSummaryVisible(true)

        // Trigger AI formulation when modal opens
        const transformedData = transformFormDataToObjectives(data)
        if (transformedData.objectives?.length) {
            formulateObjectivesWithAI(transformedData.objectives)
        }
    }

    const onChooseTrialSlot = () => {
        props.apiRequest(
            fastTrackService.chooseTrialSlot,
            {},
            { new_choice: selectedSession.value, email }
        )
    }

    useApiStatus(
        fastTrackService.chooseTrialSlot,
        null,
        false,
        () => {
            setSummaryVisible(false)
            props.apiRequest(
                fastTrackService.sendVerificationCode,
                {},
                { verification_method: "sms", email }
            )
            navigation.navigate("register/fast-track/confirm-trial", {
                user: emailB64,
            })
        },
        () => setSummaryVisible(false)
    )

    useEffect(() => setAssessmentNeedsVisible(false), [])

    useHasFinishedFastTrack(hasFinishedFastTrack, emailB64)

    return (
        <ScreenSkeleton
            errorScreenProps={{ error: props.error }}
            isLoading={initialLoading}
        >
            <View style={{ flex: 1, alignItems: "center" }}>
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData?.filter(
                        (step) => step.is_masked !== true
                    )}
                    champion={null}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    onSubmit={showRecapModal}
                    submittedMessage=""
                    serverError={null}
                    submitButtonText={"Réserver la séance"}
                    getGoToNextStep={(fn) => (goToNextStepRef.current = fn)}
                    syncFormValues={true}
                />
                <AssessmentNeedsModal
                    isVisible={assessmentNeedsVisible}
                    onClose={() => setAssessmentNeedsVisible(false)}
                    user={user}
                    onSubmit={() => {
                        setAssessmentNeedsVisible(false)
                        goToNextStepRef.current()
                    }}
                    assessmentNeeds={assessmentData.assessmentNeeds}
                    setAssessmentNeeds={(assessmentNeeds) =>
                        setAssessmentData({
                            ...assessmentData,
                            assessmentNeeds,
                        })
                    }
                />
                <DiagnosticSummaryModal
                    isVisible={summaryVisible}
                    onClose={() => setSummaryVisible(false)}
                    schoolSubjects={filteredSchoolSubjects || []}
                    user={user}
                    hoursPerWeek={hoursPerWeek}
                    onSubmit={onChooseTrialSlot}
                    isFormulateObjectivesLoading={
                        props.formulateObjectiveRequest
                    }
                    objectivesWithFormulatedGoals={
                        objectivesWithFormulatedGoals
                    }
                    isLoading={props.chooseTrialSlotRequest}
                    objectiveNeeds={props.objectiveNeeds || []}
                    selectedSession={selectedSession}
                    noHours
                />

                <StatusBar
                    style="dark"
                    backgroundColor={theme.colors.white}
                    translucent={false}
                />
            </View>
        </ScreenSkeleton>
    )
}

const mapStateToProps = (state) => {
    return {
        error: state.api.getSubscribePricing?.error,
        schoolSubjects: state.data.formData?.schoolSubjects ?? [],
        objectiveNeeds: state.data.formData?.objectiveNeeds ?? [],
        formulateObjectiveRequest: state.api.formulateObjective?.request,
        chooseTrialSlotRequest: state.api.chooseTrialSlot?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FastTrackDiagnosticScreen)
